import { useFrame, useLoader } from "@react-three/fiber";
import { useRef, useState } from "react";
import { CircleGeometry, Group, TextureLoader } from "three";

function OrbitsComponant() {
  const [
    pythonMatcap,
    firebaseMatcap,
    cSharpMatcap,
    swiftMatcap,
    dockerMatcap,
    sqlMatcap,
    reactMatcap,
    swift2Matcap,
  ] = useLoader(TextureLoader, [
    "./material/python-matcap.jpg",
    "./material/firebase-matcap.jpg",
    "./material/cSharp-matcap.jpg",
    "./material/swiftMatcap.jpg",
    "./material/dockerMatcap.jpg",
    "./material/sqlMatcap.jpg",
    "./material/reactMatcap.jpg",
    "./material/swift2Matcap.jpg",
  ]);

  const groupRef = useRef<Group>(null);
  const group2Ref = useRef<Group>(null);

  const group3Ref = useRef<Group>(null);

  const circleGeometry = new CircleGeometry(4, 128);
  const circle2Geometry = new CircleGeometry(6, 128);
  const circle3Geometry = new CircleGeometry(8, 128);

  const orbitLines = [circleGeometry, circle2Geometry, circle3Geometry];

  const [hovered, hover] = useState<boolean>(false);
  const [hovered2, hover2] = useState<boolean>(false);
  const [hovered3, hover3] = useState<boolean>(false);

  const pointerOver = (e: any) => {e.stopPropagation(); hover(true)};
  const pointerOut = () => hover(false);

  const pointerOver2 = (e: any) => {e.stopPropagation(); hover2(true)};
  const pointerOut2 = () => hover2(false);

  const pointerOver3 = (e: any) => {e.stopPropagation(); hover3(true)};
  const pointerOut3 = () => hover3(false);
  // Animation for rotation of each orbit
  useFrame((state, delta) => {
    if (groupRef.current && group2Ref.current && group3Ref.current) {
      groupRef.current.rotation.z += hovered ? delta * 0 : delta * 0.2;
      group2Ref.current.rotation.z += hovered2 ? delta * 0 : delta * -0.1;
      group3Ref.current.rotation.z += hovered3 ? delta * 0 : delta * 0.05;
    }
  });

  // function for finding x,y coordinates based on the radius and angle of a circle
  function getCircleCoordinates(
    radius: number,
    theta: number
  ): { x: number; y: number } {
    const radians = theta * (Math.PI / 180);
    const x = radius * Math.cos(radians);
    const y = radius * Math.sin(radians);

    return { x, y };
  }

  return (
    <>
      {/* Drawing orbit lines */}
      <group rotation={[1.4, 0.3, 0]}>
        {orbitLines.map((orbitLines, index) => (
          <lineSegments
            onPointerOver={
              index === 0
                ? pointerOver
                : index === 1
                ? pointerOver2
                : index === 2
                ? pointerOver3
                : undefined
            }
            onPointerOut={
              index === 0
                ? pointerOut
                : index === 1
                ? pointerOut2
                : index === 2
                ? pointerOut3
                : undefined
            }
            key={index}
          >
            <edgesGeometry args={[orbitLines]} />
            <lineBasicMaterial color={"#7C45FF"} linewidth={1} />
          </lineSegments>
        ))}
      </group>

      {/* Drawing small spheres on the orbit lines */}

      {/* orbit 1 */}
      <group
        onPointerOver={pointerOver}
        onPointerOut={pointerOut}
        ref={groupRef}
        rotation={[1.4, 0.3, 0]}
      >
        <mesh
          onPointerEnter={(e) => {
            document.body.style.cursor = "pointer";
            e.stopPropagation();
          }}
          onPointerLeave={() => {
            document.body.style.cursor = "default";
          }}
          onClick={(e) => {
            window.open("https://satr.codes/", "_blank");
            e.stopPropagation();
          }}
          visible
          position={[
            getCircleCoordinates(4, 30).x,
            getCircleCoordinates(4, 30).y,
            0,
          ]}
        >
          <sphereGeometry args={[0.3, 32, 32]} />
          <meshMatcapMaterial matcap={cSharpMatcap} />
        </mesh>
        <mesh
          visible
          position={[
            getCircleCoordinates(4, 85).x,
            getCircleCoordinates(4, 85).y,
            0,
          ]}
        >
          <sphereGeometry args={[0.15, 32, 32]} />
          <meshBasicMaterial color={"#7C45FF"} />
        </mesh>

        <mesh
          onPointerEnter={(e) => {
            document.body.style.cursor = "pointer";
            e.stopPropagation();
          }}
          onPointerLeave={() => {
            document.body.style.cursor = "default";
          }}
          onClick={(e) => {
            window.open("https://satr.codes/", "_blank");
            e.stopPropagation();
          }}
          visible
          position={[
            getCircleCoordinates(4, 290).x,
            getCircleCoordinates(4, 290).y,
            0,
          ]}
        >
          <sphereGeometry args={[0.2, 32, 32]} />
          <meshMatcapMaterial matcap={swiftMatcap} />
        </mesh>
      </group>

      {/* orbit 2 */}
      <group
        onPointerOver={pointerOver2}
        onPointerOut={pointerOut2}
        ref={group2Ref}
        rotation={[1.4, 0.3, 0]}
      >
        <mesh
          onPointerEnter={(e) => {
            document.body.style.cursor = "pointer";
            e.stopPropagation();
          }}
          onPointerLeave={() => {
            document.body.style.cursor = "default";
          }}
          onClick={(e) => {
            window.open("https://satr.codes/", "_blank");
            e.stopPropagation();
          }}
          visible
          position={[
            getCircleCoordinates(6, 330).x,
            getCircleCoordinates(6, 330).y,
            0,
          ]}
        >
          <sphereGeometry args={[0.2, 32, 32]} />
          <meshMatcapMaterial matcap={reactMatcap} />
        </mesh>

        <mesh
          visible
          position={[
            getCircleCoordinates(6, 0).x,
            getCircleCoordinates(6, 0).y,
            0,
          ]}
        >
          <sphereGeometry args={[0.15, 32, 32]} />
          <meshBasicMaterial color={"#7C45FF"} />
        </mesh>

        <mesh
          onPointerEnter={(e) => {
            document.body.style.cursor = "pointer";
            e.stopPropagation();
          }}
          onPointerLeave={() => {
            document.body.style.cursor = "default";
          }}
          onClick={(e) => {
            window.open("https://satr.codes/", "_blank");
            e.stopPropagation();
          }}
          visible
          position={[
            getCircleCoordinates(6, 18).x,
            getCircleCoordinates(6, 18).y,
            0,
          ]}
        >
          <sphereGeometry args={[0.25, 32, 32]} />
          <meshMatcapMaterial matcap={dockerMatcap} />
        </mesh>

        <mesh
          onPointerEnter={(e) => {
            document.body.style.cursor = "pointer";
            e.stopPropagation();
          }}
          onPointerLeave={() => {
            document.body.style.cursor = "default";
          }}
          onClick={(e) => {
            window.open("https://satr.codes/", "_blank");
            e.stopPropagation();
          }}
          visible
          position={[
            getCircleCoordinates(6, 65).x,
            getCircleCoordinates(6, 65).y,
            0,
          ]}
        >
          <sphereGeometry args={[0.4, 32, 32]} />
          <meshMatcapMaterial matcap={firebaseMatcap} />
        </mesh>

        <mesh
          onPointerEnter={(e) => {
            document.body.style.cursor = "pointer";
            e.stopPropagation();
          }}
          onPointerLeave={() => {
            document.body.style.cursor = "default";
          }}
          onClick={(e) => {
            window.open("https://satr.codes/", "_blank");
            e.stopPropagation();
          }}
          visible
          position={[
            getCircleCoordinates(6, 120).x,
            getCircleCoordinates(6, 120).y,
            0,
          ]}
        >
          <sphereGeometry args={[0.18, 32, 32]} />
          <meshMatcapMaterial matcap={cSharpMatcap} />
        </mesh>

        <mesh
          onPointerEnter={(e) => {
            document.body.style.cursor = "pointer";
            e.stopPropagation();
          }}
          onPointerLeave={() => {
            document.body.style.cursor = "default";
          }}
          onClick={(e) => {
            window.open("https://satr.codes/", "_blank");
            e.stopPropagation();
          }}
          visible
          position={[
            getCircleCoordinates(6, 190).x,
            getCircleCoordinates(6, 190).y,
            0,
          ]}
        >
          <sphereGeometry args={[0.4, 32, 32]} />
          <meshMatcapMaterial matcap={firebaseMatcap} />
        </mesh>

        <mesh
          visible
          position={[
            getCircleCoordinates(6, 240).x,
            getCircleCoordinates(6, 240).y,
            0,
          ]}
        >
          <sphereGeometry args={[0.2, 32, 32]} />
          <meshBasicMaterial color={"#7C45FF"} />
        </mesh>

        <mesh
          visible
          position={[
            getCircleCoordinates(6, 280).x,
            getCircleCoordinates(6, 280).y,
            0,
          ]}
        >
          <sphereGeometry args={[0.1, 32, 32]} />
          <meshBasicMaterial color={"#7C45FF"} />
        </mesh>
      </group>

      {/* orbit 3 */}
      <group
        onPointerOver={pointerOver3}
        onPointerOut={pointerOut3}
        ref={group3Ref}
        rotation={[1.4, 0.3, 0]}
      >
        <mesh
          onPointerEnter={(e) => {
            document.body.style.cursor = "pointer";
            e.stopPropagation();
          }}
          onPointerLeave={() => {
            document.body.style.cursor = "default";
          }}
          onClick={(e) => {
            window.open("https://satr.codes/", "_blank");
            e.stopPropagation();
          }}
          visible
          position={[
            getCircleCoordinates(8, 345).x,
            getCircleCoordinates(8, 345).y,
            0,
          ]}
        >
          <sphereGeometry args={[0.17, 32, 32]} />
          <meshMatcapMaterial matcap={swiftMatcap} />
        </mesh>

        <mesh
          onPointerEnter={(e) => {
            document.body.style.cursor = "pointer";
            e.stopPropagation();
          }}
          onPointerLeave={() => {
            document.body.style.cursor = "default";
          }}
          onClick={(e) => {
            window.open(
              "https://satr.codes/educational-content?program_language=Python",
              "_blank"
            );
            e.stopPropagation();
          }}
          visible
          position={[
            getCircleCoordinates(8, 10).x,
            getCircleCoordinates(8, 10).y,
            0,
          ]}
        >
          <sphereGeometry args={[0.33, 32, 32]} />
          <meshMatcapMaterial matcap={pythonMatcap} />
        </mesh>

        <mesh
          onPointerEnter={(e) => {
            document.body.style.cursor = "pointer";
            e.stopPropagation();
          }}
          onPointerLeave={() => {
            document.body.style.cursor = "default";
          }}
          onClick={(e) => {
            window.open("https://satr.codes/", "_blank");
            e.stopPropagation();
          }}
          visible
          position={[
            getCircleCoordinates(8, 50).x,
            getCircleCoordinates(8, 50).y,
            0,
          ]}
        >
          <sphereGeometry args={[0.4, 32, 32]} />
          <meshMatcapMaterial matcap={sqlMatcap} />
        </mesh>

        <mesh
          onPointerEnter={(e) => {
            document.body.style.cursor = "pointer";
            e.stopPropagation();
          }}
          onPointerLeave={() => {
            document.body.style.cursor = "default";
          }}
          onClick={(e) => {
            window.open("https://satr.codes/", "_blank");
            e.stopPropagation();
          }}
          visible
          position={[
            getCircleCoordinates(8, 105).x,
            getCircleCoordinates(8, 105).y,
            0,
          ]}
        >
          <sphereGeometry args={[0.2, 32, 32]} />
          <meshMatcapMaterial matcap={swift2Matcap} />
        </mesh>

        <mesh
          visible
          position={[
            getCircleCoordinates(8, 150).x,
            getCircleCoordinates(8, 150).y,
            0,
          ]}
        >
          <sphereGeometry args={[0.12, 32, 32]} />
          <meshBasicMaterial color={"#7C45FF"} />
        </mesh>

        <mesh
          onPointerEnter={(e) => {
            document.body.style.cursor = "pointer";
            e.stopPropagation();
          }}
          onPointerLeave={() => {
            document.body.style.cursor = "default";
          }}
          onClick={(e) => {
            window.open(
              "https://satr.codes/educational-content?program_language=Python",
              "_blank"
            );
            e.stopPropagation();
          }}
          visible
          position={[
            getCircleCoordinates(8, 210).x,
            getCircleCoordinates(8, 210).y,
            0,
          ]}
        >
          <sphereGeometry args={[0.45, 32, 32]} />
          <meshMatcapMaterial matcap={pythonMatcap} />
        </mesh>

        <mesh
          onPointerEnter={(e) => {
            document.body.style.cursor = "pointer";
            e.stopPropagation();
          }}
          onPointerLeave={() => {
            document.body.style.cursor = "default";
          }}
          onClick={(e) => {
            window.open("https://satr.codes/", "_blank");
            e.stopPropagation();
          }}
          visible
          position={[
            getCircleCoordinates(8, 260).x,
            getCircleCoordinates(8, 260).y,
            0,
          ]}
        >
          <sphereGeometry args={[0.28, 32, 32]} />
          <meshMatcapMaterial matcap={swift2Matcap} />
        </mesh>

        <mesh
          visible
          position={[
            getCircleCoordinates(8, 320).x,
            getCircleCoordinates(8, 320).y,
            0,
          ]}
        >
          <sphereGeometry args={[0.1, 32, 32]} />
          <meshBasicMaterial color={"#7C45FF"} />
        </mesh>
      </group>
    </>
  );
}

export default OrbitsComponant;
